import { Signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, Subject } from 'rxjs';
import { StateCalculators, StateTools } from '../tools/state-tools';
import { StateHolder } from './state-holder';

export abstract class SubjectHolder<S> extends StateHolder<S> {
  private readonly _calculators?: StateCalculators<S>;
  private readonly _changes$ = new Subject<S>();

  protected constructor(initialState: S, calculators?: StateCalculators<S>) {
    super(calculators ? StateTools.calculate(initialState, initialState, calculators) : initialState);
    this._calculators = calculators;
  }

  /**
   * Return the store state as a (read-only) observable, emitting the current state on subscription.
   */
  protected get state$(): Observable<S> {
    return this.changesOf(this.stateSignal);
  }

  /**
   * Updates the current state by applying the provided reducer function, then the calculators given at construction
   * time, then the calculators given for this call.
   * If the resulting state is equal to the current state, no update is performed.
   */
  protected override update(reducer: (state: S) => Partial<S>, calculators?: StateCalculators<S>): void {
    const current = this.currentState;
    const partial = reducer(current);
    if ((partial as unknown) === (current as unknown)) {
      return;
    }
    let next = StateHolder.merge(current, partial);
    if (this._calculators) {
      next = StateTools.calculate(current, next, this._calculators);
    }
    if (calculators) {
      next = StateTools.calculate(current, next, calculators);
    }
    this.commitState(current, next);
  }

  /**
   * Patches a partial state into the current state, updating only if the result differs.
   */
  protected override patch(partialState: Partial<S>, calculators?: StateCalculators<S>): void {
    return this.update(() => partialState, calculators);
  }

  /**
   * Create an observable of the mapped state, emitting the current value on subscription and every distinct value the
   * service commits after that. The mapper only re-runs when one of the state fields it reads changed.
   */
  protected compute<R>(mapper: (state: S) => R): Observable<R> {
    return this.changesOf(this.computeSignal(mapper)).pipe(takeUntilDestroyed());
  }

  /**
   * Runs the consumer with the current state and, synchronously, with every state the service commits after that.
   */
  public override watch(consumer: (state: S) => void): void {
    this.state$.pipe(takeUntilDestroyed()).subscribe(consumer);
  }

  /**
   * Runs the consumer with the current mapped value and, synchronously, whenever a commit changes it. The mapper itself
   * only re-runs when one of the state fields it reads changed.
   */
  public override watchComputed<R>(mapper: (state: S) => R, consumer: (state: R) => void): void {
    this.changesOf(this.computeSignal(mapper)).pipe(takeUntilDestroyed()).subscribe(consumer);
  }

  /**
   * The values of a computed signal as an observable: the current one on subscription, then the ones a commit changed.
   * Reading the signal untracked leaves the mapper cached whenever no field it reads moved. The commit subscription
   * opens before the current value goes out, so a consumer that commits from its own first callback still sees it.
   */
  private changesOf<R>(source: Signal<R>): Observable<R> {
    return new Observable<R>(subscriber => {
      let previous = untracked(source);
      const subscription = this._changes$.subscribe(() => {
        const value = untracked(source);
        if (value !== previous) {
          previous = value;
          subscriber.next(value);
        }
      });
      subscriber.next(previous);
      return subscription;
    });
  }

  protected override commitState(current: S, next: S): void {
    super.commitState(current, next);
    const committed = this.currentState;
    if (committed !== current) {
      this._changes$.next(committed);
    }
  }
}
